import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import debounce from 'lodash/debounce';
import AddIcon from '@material-ui/icons/Add';
import { RMSAutocomplete } from '../../../shared/components/form-controls/index';
import { RMSIconButton } from '../../components/buttons/index';
import usePermissions from 'features/shared/hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'features/shared/constants/permissionResources';
import { executeApiAction } from '../../utility/apiHelper';
import generalServices, { getVehicleById } from '../../../shared/services/generalServices';
import { useFormContext } from 'react-hook-form';
import { useVehicleDialog } from 'features/user/hooks/useVehicleDialog';

const SearchVehicleAutoComplete = ({
  name,
  label,
  onSelectionChange,
  onVehicleCreated,
  placeholder,
  defaultVehicleId = null,
  defaultVehicleSelectedCallback = null,
  disabled = false,
  showAddIcon = false,
}) => {
  const isMounted = useRef(true);
  const { setValue } = useFormContext();
  const { openVehicleDialog } = useVehicleDialog();

  const vehiclePermissions = usePermissions(PERMISSION_RESOURCES.VEHICLE);
  const canCreateVehicle = vehiclePermissions.Create;
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);

  const onAddIconClick = () => {
    if (disabled) return;
    openVehicleDialog({
      closeOnSave: true,
      onVehicleCreated: (result) => {
        const { vehicle, registration } = result || {};
        if (!isMounted.current || !vehicle?.ptsVehicleID) return;
        const option = {
          ...vehicle,
          PlateNumber: registration?.PlateNumber || '',
          PlateState: registration?.PlateState || '',
        };
        setVehicles((previous) => [
          option,
          ...previous.filter((item) => item.ptsVehicleID !== vehicle.ptsVehicleID),
        ]);
        setValue(name, vehicle.ptsVehicleID, { shouldDirty: true, shouldValidate: true });
        if (onVehicleCreated) onVehicleCreated(result);
        else onSelectionChange?.(option);
      },
    });
  };

  const searchVehicles = useCallback(async (searchText) => {
    await executeApiAction({
      setLoading,
      action: () => generalServices.getVehiclesBySearch(searchText),
      onSuccess: (response) => setVehicles(response),
    });
  }, []);

  const debouncedSearch = useMemo(
    () =>
      debounce((searchText) => {
        if (!searchText || searchText.trim().length < 3) {
          setVehicles([]);
          return;
        }

        searchVehicles(searchText);
      }, 500),
    [searchVehicles]
  );

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleDefaultVehicleId = useCallback(
    async (vehicleId) => {
      if (!vehicleId) return;

      await executeApiAction({
        setLoading,
        action: () => getVehicleById(vehicleId),
        onSuccess: (response) => {
          setVehicles((prev) => {
            const exists = prev.some((vehicle) => vehicle.ptsVehicleID === response.ptsVehicleID);
            return exists ? prev : [response, ...prev];
          });

          defaultVehicleSelectedCallback?.(response);
        },
      });
    },
    [defaultVehicleSelectedCallback]
  );

  useEffect(() => {
    handleDefaultVehicleId(defaultVehicleId);
  }, [defaultVehicleId, handleDefaultVehicleId]);

  return (
    <RMSAutocomplete
      name={name}
      label={label ?? 'Vehicle'}
      placeholder={placeholder}
      getOptionLabel={(option) =>
        [option.Make, option.Model, option.Year, option.PlateNumber].filter(Boolean).join(' - ')
      }
      optionLabel="PlateNumber"
      optionValue="ptsVehicleID"
      options={vehicles}
      onSelectionChange={(data) => onSelectionChange && onSelectionChange(data)}
      onSearch={debouncedSearch}
      disabled={disabled}
      loading={loading}
      leftContent={() =>
        showAddIcon &&
        canCreateVehicle && (
          <RMSIconButton
            icon={<AddIcon fontSize="small" />}
            tooltip="Add Vehicle"
            size="small"
            disabled={disabled}
            onClick={(event) => {
              event.stopPropagation();
              onAddIconClick?.(event);
            }}
          />
        )
      }
    />
  );
};

export default SearchVehicleAutoComplete;
